// Phase v1.3 (MUI-05 / D-COACH-08) — CoachTip speech-bubble surface.
//
// Renders the `powerup_timing` hint message. Anchored below MatchTopBar as
// a small absolute-positioned card (MatchScreen's parent stacking context
// provides the anchor). Has an in-content close-icon button — the consumer
// drives the `onDismiss` callback (typically `useCoachHint().dismiss`).
//
// Styling follows the .card recipe + Manrope 14 + line-height 1.4 +
// var(--space-3) padding per UI-SPEC. ariaLabel for the dismiss control
// is i18n-keyed (coach.hint.dismiss_label).
//
// Note: the message body is rendered via FormattedMessage with the server-
// supplied `messageKey` — the i18n strings live in apps/web/public/locales/
// {en,sv}.json (Plan Task 6 adds the 3 powerup_timing keys + the dismiss/
// button labels + cooldown toast).

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

// Stacking order (ARCA-131 / ARCA-150): the CoachTip is an advisory status
// toast and must sit BELOW the core-gameplay banners so it can never bury or
// be confused with them — AnchorPlacementPrompt (zIndex 20) and the
// empower-arming banner (zIndex 60). 15 keeps the tip above ordinary board
// content while staying under both. The cooldown-toast sibling in MatchScreen
// reuses this constant so the two coach surfaces share one source of truth.
export const COACH_TIP_Z_INDEX = 15

export interface CoachTipProps {
  /** i18n key for the tip body (e.g. `coach.powerup_timing.empower_soon`). */
  readonly messageKey: string
  /** Fires when the user taps the close-icon dismiss button. */
  readonly onDismiss: () => void
}

export function CoachTip({
  messageKey,
  onDismiss,
}: CoachTipProps): JSX.Element {
  const intl = useIntl()
  const dismissLabel = intl.formatMessage({ id: 'coach.hint.dismiss_label' })
  return (
    <div
      role="status"
      data-screen-region="coach-tip"
      className="card"
      style={{
        position: 'absolute',
        top: 'calc(var(--space-3) * 2 + 44px)',
        right: 'var(--space-3)',
        maxWidth: 280,
        padding: 'var(--space-3)',
        fontFamily: 'var(--font-family-body)',
        fontSize: 'var(--font-size-14)',
        lineHeight: 1.4,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-2)',
        zIndex: COACH_TIP_Z_INDEX,
        // Never intercept board-cell taps while visible (ARCA-131, High-sev).
        // Only the dismiss (×) control below re-enables pointer events.
        pointerEvents: 'none',
      }}
    >
      <span style={{ flex: 1 }}>
        <FormattedMessage id={messageKey} />
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={dismissLabel}
        data-testid="coach-tip-dismiss"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          color: 'currentColor',
          cursor: 'pointer',
          lineHeight: 1,
          padding: 0,
          marginLeft: 'auto',
          // Re-enable pointer events on the dismiss control only — the tip
          // container itself is pointer-events:none (see above).
          pointerEvents: 'auto',
        }}
      >
        {/* In-content close icon (ARCA-82 WS2 — a drawn icon, never a text
            close-glyph). CoachTip is an inline status tip, not a scrim modal. */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M3 3l8 8M11 3l-8 8" />
        </svg>
      </button>
    </div>
  )
}
